<script setup>
import { computed, defineProps, defineEmits, watch, onUnmounted } from "vue";
import i18n from "@/core/plugins/vue-i18n";

const props = defineProps({
  showModal: {
    type: Boolean,
    default: false
  },
  showFooter: {
    type: Boolean,
    default: true
  },
  title: {
    type: String,
    default: ""
  },
  type: {
    type: String,
    default: "default"
  },
  closeText: {
    type: String,
    default: "general.close"
  },
  useCloseIcon: {
    type: Boolean,
    default: false
  },
  disableClose: {
    type: Boolean,
    default: false
  },
  width: {
    type: String,
    default: null
  }
});
const emit = defineEmits(["toggle-modal"]);
const model = computed({
  get() {
    return props.showModal;
  },
  set(newValue) {
    emit("toggle-modal", newValue);
  }
});
const closeLabel = computed(() =>
  i18n.te(props.closeText) ? i18n.t(props.closeText) : props.closeText
);

const unwatch = watch(
  () => model.value,
  () => {
    if (model.value) {
      document.querySelector("body").style.overflow = "hidden";
      window.top.postMessage({ action: "dialogopen" }, "*");
    } else {
      document.querySelector("body").style.overflow = "inherit";
      window.top.postMessage({ action: "dialogclose" }, "*");
    }
  }
);

onUnmounted(() => unwatch());
</script>

<template>
  <v-dialog
    v-model="model"
    :width="width"
    :content-class="'bg-white dialog dialog-' + type"
    :persistent="disableClose"
    overlay-color="#000"
    overlay-opacity="0.5"
  >
    <div class="card card-custom overflow-hidden">
      <div
        class="card-header align-content-center"
        :class="{
          'card-header--small': useCloseIcon
        }"
      >
        <div class="d-flex flex-center">
          <span v-if="title" class="text-h5">
            {{ title }}
          </span>
        </div>
        <div class="card-toolbar">
          <slot name="beforeClose"></slot>

          <button
            size="sm"
            class="btn btn-secondary"
            @click="$emit('toggle-modal', disableClose)"
          >
            <i v-if="useCloseIcon" class="fal fa-close"></i>
            <span v-else>{{ closeLabel }}</span>
          </button>
        </div>
      </div>
      <div class="card-body overflow-auto">
        <slot name="content" />
      </div>

      <div v-if="showFooter" class="card-footer">
        <slot name="footer" />
      </div>
    </div>
  </v-dialog>
</template>

<style scoped lang="scss">
:deep(.dialog) {
  max-width: 90%;

  &.dialog-small {
    width: 600px;
  }
  &.dialog-default {
    width: 800px;
  }
  &.dialog-large {
    width: 1000px;
  }
  &.dialog-x-large {
    width: 1200px;
  }
}
</style>
